import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../core/auth";
import TransitionWrapper from "./transition-wrapper";
import { track } from "@amplitude/analytics-browser";
import clsx from "clsx";
import { isAndroidApp, isWeb } from "../../utils/user-agent";
import AppDownloadModal from "../app-download/app-download-modal";
import {
  HiOutlineHome,
  HiHome,
  HiOutlineHeart,
  HiHeart,
  HiOutlineStar,
  HiStar,
  HiOutlineClipboardDocumentList,
  HiClipboardDocumentList,
  HiOutlineUser,
  HiUser,
} from "react-icons/hi2";
import { postMessage } from "../../core/message";

const BottomTab = (): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 공통 탭 클릭 핸들러
  const handleTabClick = (config: {
    path: string;
    trackEvent: string;
    requiresLogin?: boolean;
    showModalOnWeb?: boolean;
    skipIfCurrentPath?: boolean;
  }) => {
    postMessage("REQ_HAPTIC_LIGHT", "");
    const {
      path,
      trackEvent,
      requiresLogin = false,
      showModalOnWeb = false,
      skipIfCurrentPath = false,
    } = config;

    // 현재 경로와 같고 skipIfCurrentPath가 true면 아무것도 하지 않음
    if (skipIfCurrentPath && location.pathname === path) {
      return;
    }

    // 트래킹
    track(trackEvent);

    // 웹에서 모달 표시가 필요한 경우
    if (showModalOnWeb && isWeb()) {
      setIsModalOpen(true);
      return;
    }

    // 로그인이 필요한 경우
    if (requiresLogin && !isLoggedIn) {
      localStorage.setItem("redirect", window.location.pathname);
      navigate("/login");
      return;
    }

    // 네비게이션
    navigate(path);
  };
  return (
    <div
      className={clsx(
        "z-50 fixed inset-x-0 bottom-0 mx-auto w-full max-w-[742px] bg-white shadow-[0px_-2px_26px_#00000026] rounded-t-3xl",
        /* 안드로이드 앱 WebView는 env(safe-area)와 실제 보이는 영역이 어긋나 바텀 아래 빈 공간이 생기는 경우가 있어 제외 */
        !isAndroidApp() && "pb-[env(safe-area-inset-bottom,0px)]",
      )}
    >
      <div className="flex h-16 justify-evenly items-center">
        {/* Home Tab */}
        <TransitionWrapper scale={0.8} opacity={0.5}>
          <div
            className={`flex flex-col items-center ${
              location.pathname === "/" ? "opacity-100" : "opacity-40"
            }`}
            onClick={() =>
              handleTabClick({
                path: "/",
                trackEvent: "홈 탭 클릭",
                skipIfCurrentPath: true,
              })
            }
          >
            {location.pathname === "/" ? (
              <HiHome size={22} color="#1562fc" />
            ) : (
              <HiOutlineHome size={22} color="#000" />
            )}
            <p className="w-12 text-center text-sm font-semibold mt-0.5">홈</p>
          </div>
        </TransitionWrapper>

        {/* Like Tab */}
        <TransitionWrapper scale={0.8} opacity={0.5}>
          <div
            className={`flex flex-col items-center ${
              location.pathname === "/like" ? "opacity-100" : "opacity-40"
            }`}
            onClick={() =>
              handleTabClick({
                path: "/like",
                trackEvent: "좋아요 탭 클릭",
              })
            }
          >
            {location.pathname === "/like" ? (
              <HiHeart size={22} color="#1562fc" />
            ) : (
              <HiOutlineHeart size={22} color="#000" />
            )}
            <p className="w-12 text-center text-sm font-semibold mt-0.5">
              좋아요
            </p>
          </div>
        </TransitionWrapper>

        {/* Conditional Tab: Team Buy or Favorites */}
        <TransitionWrapper scale={0.8} opacity={0.5}>
          <div
            className={`flex flex-col items-center ${
              location.pathname === "/explore" ? "opacity-100" : "opacity-40"
            }`}
            onClick={() =>
              handleTabClick({
                path: "/explore",
                trackEvent: "동네후기 탭 클릭",
              })
            }
          >
            {location.pathname === "/explore" ? (
              <HiStar size={22} color="#1562fc" />
            ) : (
              <HiOutlineStar size={22} color="#000" />
            )}
            <p className="w-12 text-center text-sm font-semibold mt-0.5">
              동네후기
            </p>
          </div>
        </TransitionWrapper>

        {/* Order History Tab */}
        <TransitionWrapper scale={0.8} opacity={0.5}>
          <div
            className={`flex flex-col items-center ${
              location.pathname === "/order" ? "opacity-100" : "opacity-40"
            }`}
            onClick={() =>
              handleTabClick({
                path: "/order",
                trackEvent: "주문내역 탭 클릭",
                requiresLogin: true,
                showModalOnWeb: true,
              })
            }
          >
            {location.pathname === "/order" ? (
              <HiClipboardDocumentList size={22} color="#1562fc" />
            ) : (
              <HiOutlineClipboardDocumentList size={22} color="#000" />
            )}
            <p className="w-12 text-center text-sm font-semibold mt-0.5">
              주문내역
            </p>
          </div>
        </TransitionWrapper>

        {/* My Page Tab */}
        <TransitionWrapper scale={0.8} opacity={0.5}>
          <div
            className={`flex flex-col items-center ${
              location.pathname === "/my" ? "opacity-100" : "opacity-40"
            }`}
            onClick={() =>
              handleTabClick({
                path: "/my",
                trackEvent: "마이페이지 탭 클릭",
                requiresLogin: true,
              })
            }
          >
            {location.pathname === "/my" ? (
              <HiUser size={22} color="#1562fc" />
            ) : (
              <HiOutlineUser size={22} color="#000" />
            )}
            <p className="w-12 text-center text-sm font-semibold mt-0.5 whitespace-nowrap">
              마이페이지
            </p>
          </div>
        </TransitionWrapper>
      </div>
      <AppDownloadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        where="바텀탭"
      />
    </div>
  );
};

export default BottomTab;
